// features/customer/customer.service.ts
import { get, post, put } from '../../api/api.service';
import { deleteCustomer as deleteCustomerApi } from '../../api/customer';

export { deleteCustomerApi };

export interface Customer {
  customerId?: number; // From API response
  id?: number; // For compatibility
  name: string;
  email?: string;
  phoneNumber?: string; // API uses phoneNumber
  phone?: string; // For compatibility
  address?: string;
  orgNo?: string; // From API response
  organizationNumber?: string; // For compatibility
  vatNumber?: string;
  businessType?: string;
  accountType?: string;
  city?: string;
  zipCode?: string;
  country?: string;
  ssn?: string;
  reference?: string;
  paymentTerm?: string;
  countryCode?: string;
  isActive?: boolean;
  fortnoxCustomerId?: string;
  invoiceType?: number; // Invoice delivery method type (1=Email, 2=E-Invoice, 3=Postal)
  secondaryEmailAddress?: string; // Secondary email for email delivery
  postAddress?: string; // Postal delivery address
  postCity?: string; // Postal delivery city
  postCountryCode?: string; // Postal delivery country code
  postZipCode?: string; // Postal delivery zip code
  postCountry?: string; // Postal delivery country
  language?: string; // Invoice language (sv-se or en-us)
  invoiceLanguage?: string; // Alternative field name for language
  hasAnyInvoice?: boolean; // Whether customer has any previous invoices
}

export interface InvoiceEditResponse {
  statusCode: number;
  message: string;
  response: {
    user: any;
    customers: Customer[];
    freelancers: any[];
  };
  totalCount: number;
  numberOfPages: number;
}

export interface CreateCustomerRequest {
  name: string;
  businessType: string;
  accountType?: string;
  organizationNumber?: string;
  ssn?: string;
  country: string;
  city: string;
  zipCode: string;
  addressLine1: string;
  addressLine2?: string;
  hasVATNumber: string;
  vatNumber?: string;
  vatCountryCode?: string;
  customerReference?: string;
  contactEmail?: string;
  contactPhone?: string;
  invoiceType?: string;
  ourElectronicReference?: string;
  yourElectronicReference?: string;
  globalLocationNumber?: string;
  extraField1?: string;
  extraField2?: string;
  careOf?: string;
  paymentTerms?: string;
  invoiceCurrency?: string;
  invoiceLanguage?: string;
  vatDetails?: string;
  countryCode?: string;
  // Postal delivery fields
  postAddress?: string;
  postCity?: string;
  postCountryCode?: string;
  postZipCode?: string;
  postCountry?: string;
  // Secondary email for email delivery
  secondaryEmail?: string;
}

export interface UpdateCustomerRequest extends CreateCustomerRequest {
  reference: string;
  phoneNumber: string;
  email: string;
  orgNo: string;
  id: number;
}

export interface UpdateCustomerAddressRequest {
  customerId: number;
  zipCode: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
}

export interface CreateCustomerResponse {
  statusCode: number;
  message: string;
  response: Customer;
}

export interface UpdateCustomerResponse {
  statusCode: number;
  message: string;
  response: Customer;
}

export const getInvoiceEditDataApi = async (userId: number): Promise<InvoiceEditResponse> => {
  try {
    const response = await get(`/signupuserv2/user/${userId}`);
    return response as InvoiceEditResponse;
  } catch (error) {
    throw error;
  }
};

export const createCustomerApi = async (customerData: CreateCustomerRequest): Promise<CreateCustomerResponse> => {
  try {
    // Transform paymentTerms to paymentTerm for API and remove secondary email fields to handle them separately
    const { paymentTerms, invoiceLanguage, secondaryEmail, secondaryEmailAddress, isSecondaryEmailAddress, ...rest } = customerData as any;
    
    // Convert language format
    let language = 'en-us'; // default
    if (invoiceLanguage) {
      const lowerLang = invoiceLanguage.toLowerCase();
      if (lowerLang === 'swedish' || lowerLang === 'sv-se') {
        language = 'sv-se';
      } else if (lowerLang === 'english' || lowerLang === 'en-us') {
        language = 'en-us';
      }
    }
    
    console.log('🔍 [createCustomerApi] Language Debug:');
    console.log('   Raw invoiceLanguage:', invoiceLanguage);
    console.log('   Final language for API:', language);
    
    // Extract secondary email from either field name
    const secondaryEmailValue = secondaryEmailAddress || secondaryEmail;
    
    const apiData = {
      ...rest,
      paymentTerm: paymentTerms,
      language: language,
      ...(secondaryEmailValue && {
        secondaryEmailAddress: secondaryEmailValue,
        isSecondaryEmailAddress: true
      }),
      ...(!secondaryEmailValue && isSecondaryEmailAddress === false && {
        isSecondaryEmailAddress: false
      })
    };
    
    console.log('📦 [createCustomerApi] Full API Payload:', apiData);
    
    const response = await post('/customer', apiData);
    return response as CreateCustomerResponse;
  } catch (error) {
    console.error('Create Customer API Error:', error);
    throw error;
  }
};

export const updateCustomerApi = async (customerData: UpdateCustomerRequest): Promise<UpdateCustomerResponse> => {
  try {
    // Convert language format
    let language = 'en-us'; // default
    if (customerData.invoiceLanguage) {
      const lowerLang = customerData.invoiceLanguage.toLowerCase();
      if (lowerLang === 'swedish' || lowerLang === 'sv-se') {
        language = 'sv-se';
      } else if (lowerLang === 'english' || lowerLang === 'en-us') {
        language = 'en-us';
      }
    }
    
    console.log('🔍 [updateCustomerApi] Language Debug:');
    console.log('   Raw invoiceLanguage:', customerData.invoiceLanguage);
    console.log('   Final language for API:', language);
    
    const apiData = {
      name: customerData.name,
      orgNo: customerData.orgNo || '',
      ssn: customerData.ssn || '',
      phoneNumber: customerData.phoneNumber || '',
      reference: customerData.reference || '',
      language: language,
      email: customerData.email || '',
      isVATRegistred: customerData.hasVATNumber === 'true',
      address: customerData.addressLine1 || '',
      address2: customerData.addressLine2 || '',
      co: customerData.careOf || null,
      city: customerData.city || '',
      zipCode: customerData.zipCode || '',
      country: customerData.country || 'Sweden',
      paymentTerm: customerData.paymentTerms || '30',
      accountType: customerData.businessType,
      vatNumber: customerData.vatNumber || '',
      invoiceType: parseInt(customerData.invoiceType || '1'),
      yourElectronicReference: customerData.yourElectronicReference || '',
      globalLocationNumber: customerData.globalLocationNumber || '',
      extraField1: customerData.extraField1 || '',
      extraField2: customerData.extraField2 || '',
      vatCountryCode: customerData.vatCountryCode || 'SE',
      id: customerData.id,
      countryCode: customerData.countryCode || 'SE',
      secondaryEmailAddress: (customerData as any).secondaryEmailAddress || customerData.secondaryEmail || '',
      isSecondaryEmailAddress: ((customerData as any).secondaryEmailAddress || customerData.secondaryEmail) ? true : false,
      // Postal delivery address fields - only include if they exist
      ...(customerData.postAddress && {
        postAddress: customerData.postAddress,
        postCity: customerData.postCity || '',
        postCountryCode: customerData.postCountryCode || '',
        postZipCode: customerData.postZipCode || '',
        postCountry: customerData.postCountry || '',
      }),
    };
    
    console.log('📦 [updateCustomerApi] Full API Payload:', apiData);
    
    const response = await put('/customer', apiData);
    return response as UpdateCustomerResponse;
  } catch (error) {
    console.error('Update Customer API Error:', error);
    throw error;
  }
};

export const updateCustomerAddressApi = async (addressData: UpdateCustomerAddressRequest): Promise<UpdateCustomerResponse> => {
  try {
    // Convert empty strings to null
    const apiData = {
      customerId: addressData.customerId,
      zipCode: addressData.zipCode || null,
      address: addressData.address || null,
      city: addressData.city || null,
      country: addressData.country || null
    };
    
    const response = await put('/customerv2/update-customer-address', apiData);
    return response as UpdateCustomerResponse;
  } catch (error) {
    console.error('Update Customer Address API Error:', error);
    throw error;
  }
};